import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';

import useAuth from '../../auth/useAuth';
import ordersApi from '../../api/ordersApi';
import quotesApi from '../../api/quotesApi';
import serviceOrdersApi from '../../api/serviceOrdersApi';
import { getErrorMessage } from '../../utils/errorHandler';
import CustomerOrderCard from '../../sections/client/account/CustomerOrderCard';
import CustomerServiceOrderCard from '../../sections/client/account/CustomerServiceOrderCard';

const RECENT = 3;

function SummaryCard({ icon, value, label, caption, to }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea component={RouterLink} to={to} sx={{ height: '100%' }}>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Stack
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.lighter',
                color: 'primary.dark',
              }}
            >
              {icon}
            </Stack>
            <Stack>
              <Typography variant="h4">{value ?? '—'}</Typography>
              <Typography variant="body2">{label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// Seccion del panel con sus ultimos elementos y un enlace a la lista completa.
function RecentSection({ title, to, loading, empty, children }) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{title}</Typography>
        <Button size="small" component={RouterLink} to={to}>
          Ver todos
        </Button>
      </Stack>
      {loading && <Skeleton variant="rounded" height={160} />}
      {!loading && empty}
      {!loading && children}
    </Stack>
  );
}

// Panel del cliente: resumen y seguimiento de lo que hizo en la pagina web (pedidos, ordenes de
// servicio tecnico y cotizaciones), con acceso a cada historial completo.
export default function CustomerDashboardPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [quotesTotal, setQuotesTotal] = useState(null);

  useEffect(() => {
    Promise.all([
      ordersApi.getMyOrders({ per_page: 50 }),
      serviceOrdersApi.getMyServiceOrders({ per_page: 50 }),
      quotesApi.getMyQuotes({ per_page: 1 }),
    ])
      .then(([ordersRes, servicesRes, quotesRes]) => {
        setOrders(ordersRes.data.data);
        setServiceOrders(servicesRes.data.data);
        setQuotesTotal(quotesRes.data.meta?.total ?? quotesRes.data.data.length);
      })
      .catch((error) => enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar tu panel.'), { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  const pendingOrders = orders.filter((order) => order.status?.name === 'pendiente').length;
  const activeServices = serviceOrders.filter((order) => order.status?.name !== 'entregado').length;

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 4, justifyContent: 'space-between', alignItems: { sm: 'center' } }}
      >
        <div>
          <Typography variant="h4">Hola, {user?.name || 'cliente'} 👋</Typography>
          <Typography color="text.secondary">Sigue tus pedidos, servicios técnicos y cotizaciones.</Typography>
        </div>
        <Button variant="outlined" startIcon={<PersonOutlineIcon />} component={RouterLink} to="/mi-cuenta">
          Mi cuenta
        </Button>
      </Stack>

      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <SummaryCard
            icon={<ShoppingBagOutlinedIcon />}
            value={loading ? null : orders.length}
            label="Pedidos"
            caption={loading ? ' ' : `${pendingOrders} pendiente(s) de pago`}
            to="/mis-pedidos"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SummaryCard
            icon={<BuildOutlinedIcon />}
            value={loading ? null : serviceOrders.length}
            label="Servicios técnicos"
            caption={loading ? ' ' : `${activeServices} en curso`}
            to="/mis-ordenes-servicio"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SummaryCard
            icon={<RequestQuoteOutlinedIcon />}
            value={loading ? null : quotesTotal}
            label="Cotizaciones"
            caption="Armadas en el cotizador de PC"
            to="/mis-cotizaciones"
          />
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentSection
            title="Mis últimos pedidos"
            to="/mis-pedidos"
            loading={loading}
            empty={
              orders.length === 0 && (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" component={RouterLink} to="/catalogo">
                      Ver catálogo
                    </Button>
                  }
                >
                  Aún no tienes pedidos.
                </Alert>
              )
            }
          >
            {orders.slice(0, RECENT).map((order) => (
              <CustomerOrderCard key={order.id} order={order} compact />
            ))}
          </RecentSection>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <RecentSection
            title="Mis servicios técnicos"
            to="/mis-ordenes-servicio"
            loading={loading}
            empty={
              serviceOrders.length === 0 && (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" component={RouterLink} to="/servicio-tecnico">
                      Solicitar
                    </Button>
                  }
                >
                  Aún no tienes solicitudes de servicio.
                </Alert>
              )
            }
          >
            {serviceOrders.slice(0, RECENT).map((order) => (
              <CustomerServiceOrderCard key={order.id} order={order} compact />
            ))}
          </RecentSection>
        </Grid>
      </Grid>
    </Container>
  );
}
